// Audio-driven (Web Audio) viseme weights — language-agnostic.
//
// Replaces the grapheme→viseme mapping (visemeMapper.getActiveVisemeAt) which
// mis-fired in Portuguese (no phoneme layer: missing `c`, `h` read as English,
// wrong digraphs/nasals). Here the mouth is driven by the real audio waveform's
// frequency spectrum, so it works for any language.
//
// Pure function: given a live AnalyserNode, returns mouth-only ARKit52 weights.
// Only mouth morphs are touched; eyes/brows/blink stay owned by other hooks.

import { ARKIT52_MORPHS, type ARKit52Morph } from "@/lib/visemeMapper";

// ── Tuning ──────────────────────────────────────────────────────────────────
export const FFT_SIZE = 1024; // → 512 frequency bins
export const SMOOTHING_TIME_CONSTANT = 0.12; // light analyser smoothing — FFT de-noise only (our dt-based lerp owns the rest)
export const SILENCE_THRESHOLD = 0.05; // normalized energy below this → mouth closed

// Jaw opening as a non-linear function of overall energy.
export const JAW_GAIN = 1.5;
export const JAW_CURVE = 0.7; // <1 → opens fast at low energy, eases near the top
export const JAW_MAX = 0.6; // never fully unhinge the jaw

// Spectral bands (Hz). Low energy ≈ rounded vowels; high energy ≈ sibilants.
export const LOW_BAND_MAX_HZ = 500;
export const HIGH_BAND_MIN_HZ = 2500;

// Dominance thresholds + modest weight ceilings (avoid over-articulation).
export const LOW_DOMINANCE_MIN = 0.55;
export const HIGH_DOMINANCE_MIN = 0.5;

// Direction-aware smoothing time-constants (ms). Converted to a per-frame alpha
// each frame via alphaEff = 1 − exp(−dt/τ), so the perceived smoothing time is
// frame-rate independent. A fixed per-frame alpha (the old 0.7/0.4) doubled its
// effective τ when the native WKWebView ran below the preview's 60fps — the
// source of the on-device sluggishness. Opening is the salient event (tight τ);
// closing a touch slower to avoid inter-syllable flicker.
export const ATTACK_TAU_MS = 20; // mouth opening — tight
export const RELEASE_TAU_MS = 50; // mouth closing — slightly smoother
// Guards a pathological frame delta (tab backgrounded, GC pause): a huge dt
// would drive alphaEff→1 and teleport the mouth. Clamp so one stalled frame
// can't snap it.
export const DT_CLAMP_MAX_MS = 100;
// Phase compensation. The analyser taps the signal as it ENTERS the Web Audio
// graph; it only becomes audible ctx.outputLatency later, so the mouth leads
// the sound by that much. Small on the preview (Safari/desktop), large on the
// native WKWebView (bigger output buffers) — the source of "mouth opens before
// audio". We delay the visemes by the resolved output latency (see
// resolvePhaseDelayMs). Last-resort delay when the platform reports nothing
// trustworthy, and an upper clamp so a bogus value can't desync the other way.
// PROVISIONAL (Build 16 device-test target): the native WKWebView reports
// outputLatency as 0/NaN and falls through to this constant, yet its output
// buffers are larger than the old 120ms estimate — the source of "mouth opens
// before audio". Bumped to 160ms as a conservative best-effort until we read
// the real ctx.outputLatency / baseLatency on-device (see the one-time
// instrumentation in useLipSync). Re-tune once we have the measured number.
export const PHASE_FALLBACK_MS = 160;
export const PHASE_DELAY_MAX_MS = 250;
export const ROUND_MAX = 0.5; // mouthFunnel / mouthPucker ceiling
export const SPREAD_MAX = 0.3; // mouthStretch / mouthSmile ceiling
export const SIBILANT_JAW_REDUCTION = 0.5; // close jaw on high-frequency dominance

// Returns mouth-only ARKit52 weights for the current analyser frame. Empty
// object means "neutral" (silence, or no usable signal). Never throws.
export function analyserToVisemeWeights(
  analyser: AnalyserNode,
): Partial<Record<ARKit52Morph, number>> {
  const bins = analyser.frequencyBinCount;
  const data = new Uint8Array(bins);
  analyser.getByteFrequencyData(data);

  let sum = 0;
  for (let i = 0; i < bins; i++) sum += data[i];
  const energy = sum / bins / 255; // 0..1

  if (energy < SILENCE_THRESHOLD) return {};

  // Split the spectrum into a low and a high band by real frequency.
  const sampleRate = analyser.context.sampleRate;
  const hzPerBin = sampleRate / 2 / bins;
  const lowMaxBin = Math.max(1, Math.min(bins, Math.floor(LOW_BAND_MAX_HZ / hzPerBin)));
  const highMinBin = Math.max(0, Math.min(bins, Math.floor(HIGH_BAND_MIN_HZ / hzPerBin)));

  let lowSum = 0;
  let lowN = 0;
  for (let i = 0; i < lowMaxBin; i++) {
    lowSum += data[i];
    lowN++;
  }
  let highSum = 0;
  let highN = 0;
  for (let i = highMinBin; i < bins; i++) {
    highSum += data[i];
    highN++;
  }
  const low = lowN ? lowSum / lowN / 255 : 0;
  const high = highN ? highSum / highN / 255 : 0;
  const bandTotal = low + high + 1e-6;
  const lowDominance = low / bandTotal;
  const highDominance = high / bandTotal;

  const weights: Partial<Record<ARKit52Morph, number>> = {};

  // Jaw from overall energy (non-linear curve, capped).
  let jaw = Math.min(JAW_MAX, Math.pow(energy, JAW_CURVE) * JAW_GAIN);

  // Rounded vowels: low-frequency dominance → funnel + pucker, scaled by energy.
  if (lowDominance > LOW_DOMINANCE_MIN) {
    const round = Math.min(ROUND_MAX, (lowDominance - LOW_DOMINANCE_MIN) * 2 * energy + 0.15);
    weights.mouthFunnel = round;
    weights.mouthPucker = round * 0.7;
  }

  // Sibilants: high-frequency dominance → slight spread + smile, narrower jaw.
  if (highDominance > HIGH_DOMINANCE_MIN) {
    const spread = Math.min(SPREAD_MAX, (highDominance - HIGH_DOMINANCE_MIN) * 2 * energy + 0.1);
    weights.mouthStretchLeft = spread;
    weights.mouthStretchRight = spread;
    weights.mouthSmileLeft = spread * 0.5;
    weights.mouthSmileRight = spread * 0.5;
    jaw *= 1 - SIBILANT_JAW_REDUCTION;
  }

  weights.jawOpen = jaw;
  return weights;
}

// Resolves the viseme phase delay (ms) for the given context. Prefers
// ctx.outputLatency, but ONLY when it's a finite, strictly-positive number —
// WKWebView can report 0 / NaN / undefined. Falls back to baseLatency*2, then
// to PHASE_FALLBACK_MS. Result is clamped to [0, PHASE_DELAY_MAX_MS].
export function resolvePhaseDelayMs(ctx: AudioContext): number {
  const out = ctx.outputLatency;
  let delaySec: number;
  if (typeof out === "number" && Number.isFinite(out) && out > 0) {
    delaySec = out;
  } else {
    const base = ctx.baseLatency;
    if (typeof base === "number" && Number.isFinite(base) && base > 0) {
      // PROVISIONAL: base*2 alone under-delays the native WKWebView (small
      // baseLatency, large output buffer) → mouth leads the audio. Floor it at
      // PHASE_FALLBACK_MS so the estimate can't fall below the native minimum.
      delaySec = Math.max(base * 2, PHASE_FALLBACK_MS / 1000);
    } else {
      delaySec = PHASE_FALLBACK_MS / 1000;
    }
  }
  return Math.min(PHASE_DELAY_MAX_MS, Math.max(0, delaySec * 1000));
}

// Like smoothViseme but direction-aware AND frame-rate independent: morphs
// ramping UP use the attack τ, morphs ramping DOWN use the release τ, each
// converted to a per-frame alpha from the real frame delta (alphaEff =
// 1 − exp(−dt/τ)). Same wall-clock smoothing at 30fps and 60fps.
export function smoothVisemeAsymmetric(
  current: Partial<Record<ARKit52Morph, number>>,
  target: Partial<Record<ARKit52Morph, number>>,
  dtMs: number,
  attackTauMs: number,
  releaseTauMs: number,
): Record<ARKit52Morph, number> {
  const attackAlpha = 1 - Math.exp(-dtMs / attackTauMs);
  const releaseAlpha = 1 - Math.exp(-dtMs / releaseTauMs);
  const result = {} as Record<ARKit52Morph, number>;
  for (const morph of ARKIT52_MORPHS) {
    const cur = current[morph] ?? 0;
    const tgt = target[morph] ?? 0;
    const alpha = tgt > cur ? attackAlpha : releaseAlpha;
    result[morph] = cur + (tgt - cur) * alpha;
  }
  return result;
}
